import { useState, useEffect } from "react"
import { useTranslation } from 'react-i18next'
import { useNavigate } from "react-router-dom"
import { Dialog, DialogTrigger, DialogPortal } from "@/components/ui/dialog"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { X, Eye, EyeOff, Instagram, User, LogOut, ArrowLeft } from "lucide-react"
import { cn } from "@/lib/utils"
import { auth, signOutUser } from "@/lib/firebase"
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth"
import { useAuthState } from "@/hooks/useAuth"
import { handleUserData } from '@/lib/firebase'
import { useAnalytics } from '@/hooks/useAnalytics'
import { logAuthAttempt } from '@/lib/securityLogging'
import { trackTikTokSignUp } from '@/lib/tiktokPixel'
import { trackSignUp as trackMetaPixelSignUp } from '@/lib/metaPixel'
import { Keyboard } from '@capacitor/keyboard'
import { getPlatform } from '@/lib/platform'
import { SignupFieldInfoDialog } from './SignupFieldInfoDialog'
import { doc, updateDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { getDeviceInfo, logDeviceInfoDebug } from '@/lib/deviceInfo'
import { logDeviceInfoToFirestore } from '@/lib/deviceLogging'

const authModalPrimaryButtonClassName =
  'w-full h-14 text-base font-medium rounded-[0.6rem]'

interface AuthModalProps {
  children?: React.ReactNode
  onSuccess?: () => void
  isOpen?: boolean
  onOpenChange?: (open: boolean) => void
  dismissible?: boolean
  defaultMode?: 'login' | 'signup'
  source?: string
  preAuthMessage?: string // New optional parameter for custom message
  showPreAuth?: boolean // New parameter to control whether to show pre-auth step
  artistDisplayName?: string // Artist's display name for custom signup description
  artistFirstName?: string // Artist's first name
  artistLastName?: string // Artist's last name
  artistHasBookingSystem?: boolean // Whether artist has booking system enabled
}

export function AuthModal({ 
  children, 
  onSuccess, 
  isOpen: controlledIsOpen, 
  onOpenChange: controlledOnOpenChange,
  dismissible = true,
  defaultMode = 'login',
  source = 'unknown',
  preAuthMessage, // New parameter
  showPreAuth = false, // New parameter, defaults to false for backward compatibility

}: AuthModalProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuthState()
  const { trackSignUp, trackSignIn, trackAuthPrompt, trackAuthDismiss } = useAnalytics()
  
  // State management
  const [internalIsOpen, setInternalIsOpen] = useState(false)
  const [isLogin, setIsLogin] = useState(defaultMode === 'login')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPreAuthStep, setShowPreAuthStep] = useState(showPreAuth) // New state for pre-auth step
  const [keyboardHeight, setKeyboardHeight] = useState(0)
  const [showPasswordReset, setShowPasswordReset] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetStep, setResetStep] = useState<'input' | 'confirmation'>('input')
  const [showInstagramInfo, setShowInstagramInfo] = useState(false)
  const [showSignupSuccess, setShowSignupSuccess] = useState(false)
  const [newUserId, setNewUserId] = useState<string | null>(null)
  const [referralSource, setReferralSource] = useState('')
  const [referralSourceOther, setReferralSourceOther] = useState('')
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    instagramHandle: ''
  })

  // Determine if this is controlled or uncontrolled
  const isControlled = controlledIsOpen !== undefined
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen
  const setIsOpen = isControlled ? controlledOnOpenChange : setInternalIsOpen

  // Controlled modal: parent may change defaultMode between opens; initial useState alone won't pick that up.
  useEffect(() => {
    if (!isControlled || !isOpen) return
    setIsLogin(defaultMode === 'login')
  }, [isControlled, isOpen, defaultMode])

  // Track auth prompt when modal opens
  useEffect(() => {
    if (isOpen && !user && !authLoading) {
      trackAuthPrompt(source, 'auto_prompt')
    }
  }, [isOpen, user, authLoading, source, trackAuthPrompt])

  // Reset signup success state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setShowSignupSuccess(false)
      setNewUserId(null)
      setReferralSource('')
      setReferralSourceOther('')
    }
  }, [isOpen])

  // Reset signup success state when user logs out (but only if not in the middle of signup flow)
  useEffect(() => {
    if (!user && !authLoading && showSignupSuccess) {
      setShowSignupSuccess(false)
      setNewUserId(null)
      setReferralSource('')
      setReferralSourceOther('')
    }
  }, [user, authLoading, showSignupSuccess])

  // Safety check: reset signup success if there's no newUserId
  useEffect(() => {
    if (showSignupSuccess && !newUserId) {
      setShowSignupSuccess(false)
      setReferralSource('')
      setReferralSourceOther('')
    }
  }, [showSignupSuccess, newUserId])

  // Keyboard tracking for iOS native - only run on iOS to avoid double keyboard handling
  useEffect(() => {
    const platform = getPlatform()
    if (platform !== 'ios') return

    let showListener: any
    let hideListener: any

    const setupKeyboardListeners = async () => {
      showListener = await Keyboard.addListener('keyboardWillShow', (info) => {
        setKeyboardHeight(info.keyboardHeight)
      })
      hideListener = await Keyboard.addListener('keyboardWillHide', () => {
        setKeyboardHeight(0)
      })
    }

    void setupKeyboardListeners()

    return () => {
      void showListener?.remove()
      void hideListener?.remove()
    }
  }, [])



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email.trim())) {
      setError(t('auth.invalidEmail'))
      return
    }
    
    // Validate password length
    if (formData.password.length < 6) {
      setError(t('auth.weakPassword'))
      return
    }
    
    // If it's signup, validate Instagram handle
    if (!isLogin) {
      // Validate Instagram handle
      if (!formData.instagramHandle.trim()) {
        setError(t('auth.instagramRequired'))
        return
      }
    }
    
    // Proceed with authentication
    await performAuthentication()
  }


  const handlePreAuthContinue = () => {
    setShowPreAuthStep(false)
    setIsLogin(false) // Default to signup when coming from pre-auth
  }

  const handlePreAuthLogin = () => {
    setShowPreAuthStep(false)
    setIsLogin(true) // Set to login when user chooses to login
  }


  const performAuthentication = async () => {
    setLoading(true)
    setError(null)

    try {
      let userCredential
      
      if (isLogin) {
        userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password)
        
        // Get and log device info on login
        const deviceInfo = await getDeviceInfo()
        console.log('[AuthModal] Login - Device Info:', deviceInfo)
        logDeviceInfoDebug(deviceInfo)
        
        // Log device info to Firestore immediately on login
        await logDeviceInfoToFirestore(userCredential.user.uid)
        
        // Track sign in
        await trackSignIn('email', source)
        // Log successful login
        await logAuthAttempt('login', formData.email, userCredential.user.uid)
        
        // For login, just update lastLogin
        await handleUserData(userCredential.user, {})
      } else {
        userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password)
        
        // Get and log device info on signup
        const deviceInfo = await getDeviceInfo()
        console.log('[AuthModal] Signup - Device Info:', deviceInfo)
        logDeviceInfoDebug(deviceInfo)
        
        // Log device info to Firestore immediately on signup
        await logDeviceInfoToFirestore(userCredential.user.uid)
        
        // Show success dialog for signup FIRST (before any tracking that might fail)
        setNewUserId(userCredential.user.uid)
        setShowSignupSuccess(true)
        setFormData({ email: '', password: '', instagramHandle: '' })
        
        // Track sign up (non-blocking - don't fail auth if this fails)
        try {
          await trackSignUp('email', source, !!formData.instagramHandle.trim())
        } catch (trackError) {
          console.error('Analytics tracking failed (non-critical):', trackError);
        }
        
        // Track TikTok sign up event (non-blocking - don't fail auth if this fails)
        try {
          await trackTikTokSignUp({
            email: formData.email,
            userId: userCredential.user.uid,
            content_type: 'email_signup',
            has_instagram: !!formData.instagramHandle.trim()
          });
        } catch (ttError) {
          console.error('TikTok pixel tracking failed (non-critical):', ttError);
        }
        
        // Track Meta Pixel sign up event (non-blocking - don't fail auth if this fails)
        try {
          trackMetaPixelSignUp(
            formData.email,
            'email',
            !!formData.instagramHandle.trim()
          );
        } catch (metaError) {
          console.error('Meta Pixel tracking failed (non-critical):', metaError);
        }
        
        // Log successful signup (non-blocking)
        try {
          await logAuthAttempt('signup', formData.email, userCredential.user.uid)
        } catch (logError) {
          console.error('Security logging failed (non-critical):', logError);
        }
        
        // For signup, include createdAt and instagram
        const additionalData: { instagram: string; createdAt: boolean } = {
          createdAt: true, // This signals handleUserData to set the timestamp
          instagram: formData.instagramHandle.trim()
        }
        
        // Save user data (non-blocking - don't fail auth if this fails)
        try {
          await handleUserData(userCredential.user, additionalData)
        } catch (userDataError) {
          console.error('User data saving failed (non-critical):', userDataError);
        }
        
        return // Don't close modal yet, wait for referral source
      }

      // (PWA first-launch tracking removed)

      if (!isControlled) {
        setInternalIsOpen(false)
      }
      setFormData({ email: '', password: '', instagramHandle: '' })
      
      onSuccess?.()
    } catch (error: any) {
      setError(getErrorMessage(error.code))
    } finally {
      setLoading(false)
    }
  }

  const handleSignOut = async () => {
    try {
      // Clean up iOS-specific resources first
      const { cleanupIosOnSignOut } = await import('@/lib/notifications')
      const { Capacitor } = await import('@capacitor/core')
      const isIOSNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios'
      
      if (isIOSNative) {
        await cleanupIosOnSignOut()
      }
      
      // Sign out from Firebase
      await signOutUser()
      if (!isControlled) {
        setInternalIsOpen(false)
      }
    } catch (error: any) {
      setError(t('auth.signOutError') + ': ' + (error?.message || 'Unknown error'))
    }
  }

  const handleCloseModal = () => {
    if (dismissible) {
      trackAuthDismiss(source, 'auto_prompt', 'close')
      if (!isControlled) {
        setInternalIsOpen(false)
      } else {
        setIsOpen?.(false)
      }
      setFormData({ email: '', password: '', instagramHandle: '' })
      setError(null)
      setShowSignupSuccess(false)
      setNewUserId(null)
      setReferralSource('')
      setReferralSourceOther('')
    }
  }

  const handleBack = () => {
    if (!dismissible) {
      navigate(-1) // Go back to previous page
    }
  }

  const getErrorMessage = (errorCode: string): string => {
    switch (errorCode) {
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        return t('auth.invalidCredentials')
      case 'auth/email-already-in-use':
        return t('auth.emailInUse')
      case 'auth/weak-password':
        return t('auth.weakPassword')
      case 'auth/invalid-email':
        return t('auth.invalidEmail')
      case 'auth/invalid-phone-number':
        return t('auth.invalidPhoneNumber')
      default:
        return t('auth.genericError')
    }
  }


  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (error) setError(null)
  }

  const isSignupFormValid = (): boolean => {
    if (isLogin) return true
    return !!(
      formData.email.trim() &&
      formData.password.trim() &&
      formData.instagramHandle.trim()
    )
  }

  const isLoginFormValid = (): boolean => {
    return !!(
      formData.email.trim() &&
      formData.password.trim()
    )
  }

  const handleResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(resetEmail.trim())) {
      setError(t('auth.invalidEmail'))
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Send password reset email via Firebase
      // Note: Firebase will always succeed even if email doesn't exist (security best practice)
      await sendPasswordResetEmail(auth, resetEmail.trim())
      console.log('Password reset email sent to:', resetEmail)
      setResetStep('confirmation')
    } catch (err: any) {
      console.error('Password reset error:', err)
      // Don't expose whether email exists or not for security
      // Show generic success message anyway
      setResetStep('confirmation')
    } finally {
      setLoading(false)
    }
  }

  const handleResetClose = () => {
    if (!dismissible) {
      // If modal is not dismissible, navigate to getmoretats.com instead
      window.location.href = 'https://www.getmoretats.com'
    } else {
      setShowPasswordReset(false)
      setResetEmail('')
      setResetStep('input')
      setError(null)
    }
  }

  const handleResetBack = () => {
    if (!dismissible) {
      // If modal is not dismissible, navigate to getmoretats.com instead
      window.location.href = 'https://www.getmoretats.com'
    } else {
      setShowPasswordReset(false)
      setResetEmail('')
      setResetStep('input')
      setError(null)
    }
  }

  const handleReferralSourceSubmit = async () => {
    if (!newUserId || !referralSource) return
    
    setLoading(true)
    
    try {
      // Save referral source to user document
      const userRef = doc(db, 'users', newUserId)
      const finalReferralSource = referralSource === 'Other' && referralSourceOther.trim() 
        ? referralSourceOther.trim() 
        : referralSource
      
      await updateDoc(userRef, {
        referralSource: finalReferralSource
      })
      
      // Close success dialog and auth modal
      setShowSignupSuccess(false)
      setNewUserId(null)
      setReferralSource('')
      setReferralSourceOther('')
      
      if (!isControlled) {
        setInternalIsOpen(false)
      } else {
        setIsOpen?.(false)
      }
      
      onSuccess?.()
    } catch (error) {
      console.error('Error saving referral source:', error)
      // Still close the dialog even if saving fails
      setShowSignupSuccess(false)
      setNewUserId(null)
      setReferralSource('')
      setReferralSourceOther('')
      
      if (!isControlled) {
        setInternalIsOpen(false)
      } else {
        setIsOpen?.(false)
      }
      
      onSuccess?.()
    } finally {
      setLoading(false)
    }
  }

  // If user is authenticated and this is a controlled modal, don't show anything (unless showing signup success)
  if (user && !authLoading && isControlled && !showSignupSuccess) {
    return null
  }

  // If user is authenticated and this is uncontrolled, show the authentication status (unless showing signup success)
  if (user && !authLoading && !isControlled && !showSignupSuccess) {
    return (
      <Dialog open={isOpen} onOpenChange={setInternalIsOpen}>
        <DialogTrigger asChild>
          {children}
        </DialogTrigger>
        <DialogPortal>
          <DialogPrimitive.Overlay
            className={cn(
              "fixed inset-0 z-[110] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            )}
          />
          <DialogPrimitive.Content
            className={cn(
              "fixed left-0 right-0 z-[110] border-none bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] rounded-[var(--card-radius)] max-h-[90vh] p-0 overflow-hidden mx-auto",
              "max-w-[calc(var(--max-content-width)+var(--min-side-padding)*2)]"
            )}
            style={{
              top: keyboardHeight > 0 
                ? `calc((100vh - ${keyboardHeight}px) / 2)` 
                : '50%',
              transform: 'translateY(-50%)',
              transition: 'top 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 pb-4">
              <h2 className="text-xl font-semibold text-foreground">
                {t('auth.account')}
              </h2>
              <DialogPrimitive.Close className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-0 disabled:pointer-events-none">
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            </div>

            {/* Authentication Status */}
            <div className="px-6 pb-6">
              <div className="flex-1 flex flex-col justify-center">
                <div className="space-y-3">
                  {/* Logged in status */}
                  <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-[var(--card-radius)]">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="text-base font-medium text-foreground">
                        {t('auth.loggedInAs')}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {user.email}
                      </div>
                    </div>
                    <span className="text-sm text-primary font-medium">
                      ✓ {t('auth.loggedIn')}
                    </span>
                  </div>

                  {/* Sign out button */}
                  <Button
                    variant="outline"
                    className="w-full justify-start h-16 text-base font-medium"
                    onClick={handleSignOut}
                  >
                    <div className="flex items-center gap-4 w-full">
                      <LogOut className="w-6 h-6 text-muted-foreground" />
                      <span>{t('auth.signOut')}</span>
                    </div>
                  </Button>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-[var(--card-radius)] mt-4">
                  {error}
                </div>
              )}
            </div>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
    )
  }

  // If user is not authenticated, show the pre-auth step first (if enabled), then login/signup form
  return (
    <>
      {/* Pre-Auth Information Dialog */}
      <Dialog open={isOpen && showPreAuthStep} onOpenChange={dismissible ? setIsOpen : undefined}>
        {!isControlled && showPreAuth && <DialogTrigger asChild>{children}</DialogTrigger>}
        <DialogPortal>
          <DialogPrimitive.Overlay
            className={cn(
              "fixed inset-0 z-[110] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            )}
          />
          <DialogPrimitive.Content
            className={cn(
              "fixed left-0 right-0 z-[110] border-none bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] rounded-[var(--card-radius)] max-h-[90vh] p-0 overflow-hidden mx-auto",
              "max-w-[calc(var(--max-content-width)+var(--min-side-padding)*2)]"
            )}
            style={{
              top: keyboardHeight > 0 
                ? `calc((100vh - ${keyboardHeight}px) / 2)` 
                : '50%',
              transform: 'translateY(-50%)',
              transition: 'top 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 pb-4">
              <div className="flex items-center gap-3">
                {!dismissible && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBack}
                    className="p-2 -ml-2"
                  >
                    <ArrowLeft className="h-5 w-5" />
                    <span className="sr-only">Go back</span>
                  </Button>
                )}
                <h2 className="text-xl font-semibold text-foreground">
                  {t('auth.preAuth.title')}
                </h2>
              </div>
              {dismissible && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCloseModal}
                  className="p-2 -mr-2"
                >
                  <X className="h-5 w-5" />
                  <span className="sr-only">Close</span>
                </Button>
              )}
            </div>

            {/* Pre-Auth Content */}
            <div className="px-6 pb-6">
              <div className="space-y-6">
                {/* Custom message or default */}
                <div className="text-center space-y-3">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                    <User className="w-8 h-8 text-primary" />
                  </div>
                  <p className="text-muted-foreground text-base leading-relaxed">
                    {preAuthMessage || t('auth.preAuth.defaultMessage')}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  {/* Primary Register Button */}
                  <Button
                    onClick={handlePreAuthContinue}
                    className={authModalPrimaryButtonClassName}
                  >
                    {t('auth.preAuth.register')}
                  </Button>

                  {/* Secondary Login Button */}
                  <Button
                    onClick={handlePreAuthLogin}
                    variant="ghost"
                    className="w-full text-sm text-muted-foreground hover:text-foreground"
                  >
                    {t('auth.preAuth.alreadyHaveAccount')}
                  </Button>
                </div>
              </div>
            </div>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>

      {/* Main Auth Dialog */}
      <Dialog open={isOpen && !showPreAuthStep && !showPasswordReset} onOpenChange={dismissible ? setIsOpen : undefined}>
        {!isControlled && !showPreAuth && <DialogTrigger asChild>{children}</DialogTrigger>}
        <DialogPortal>
          {/* Custom overlay with sheet-like dimming */}
          <DialogPrimitive.Overlay
            className={cn(
              "fixed inset-0 z-[110] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            )}
          />
          {/* Custom content */}
          <DialogPrimitive.Content
            className={cn(
              "fixed left-0 right-0 z-[110] border-none bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] rounded-[var(--card-radius)] max-h-[90vh] p-0 overflow-hidden mx-auto",
              "max-w-[calc(var(--max-content-width)+var(--min-side-padding)*2)]"
            )}
            style={{
              top: keyboardHeight > 0 
                ? `calc((100vh - ${keyboardHeight}px) / 2)` 
                : '50%',
              transform: 'translateY(-50%)',
              transition: 'top 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 pb-4">
              <div className="flex items-center gap-3">
                {/* Back button for pre-auth flow or non-dismissible modals */}
                {(showPreAuth || !dismissible) && !showSignupSuccess && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => showPreAuth ? setShowPreAuthStep(true) : handleBack()}
                    className="p-2 -ml-2"
                  >
                    <ArrowLeft className="h-5 w-5" />
                    <span className="sr-only">Go back</span>
                  </Button>
                )}
                <div className="flex flex-col">
                  <h2 className="text-xl font-semibold text-foreground">
                    {showSignupSuccess ? 'All set! 🫶' : (isLogin ? t('auth.login') : t('auth.signup'))}
                  </h2>
                  {showSignupSuccess && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Your account was created.
                    </p>
                  )}
                </div>
              </div>
              {dismissible && !showSignupSuccess && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCloseModal}
                  className="p-2 -mr-2"
                >
                  <X className="h-5 w-5" />
                  <span className="sr-only">Close</span>
                </Button>
              )}
            </div>

            {/* Signup Success Content */}
            {showSignupSuccess ? (
              <div className="px-6 pb-6 space-y-4">
                {/* Referral Source Dropdown */}
                <div className="space-y-3 pt-2 max-w-md mx-auto">
                  <label className="text-sm font-medium text-foreground text-center block mb-4">
                    We would love to know how you heard about GetMoreTats?
                  </label>
                  <select
                    value={referralSource}
                    onChange={(e) => {
                      setReferralSource(e.target.value)
                      if (e.target.value !== 'Other') {
                        setReferralSourceOther('')
                      }
                    }}
                    className="w-full bg-muted/50 rounded-md px-3 py-3 text-foreground border border-border focus:outline-none focus:ring-0"
                  >
                    <option value="">Select an option...</option>
                    <option value="Instagram">Instagram</option>
                    <option value="TikTok">TikTok</option>
                    <option value="Friends">Friends</option>
                    <option value="Tattoo Artist">Tattoo Artist</option>
                    <option value="Google Search">Google Search</option>
                    <option value="Other">Other</option>
                  </select>
                  
                  {/* Other text input - shown when "Other" is selected */}
                  {referralSource === 'Other' && (
                    <Input
                      type="text"
                      value={referralSourceOther}
                      onChange={(e) => setReferralSourceOther(e.target.value)}
                      placeholder="Please specify..."
                      className="w-full border-border"
                    />
                  )}
                </div>

                {/* Continue Button */}
                <Button
                  onClick={handleReferralSourceSubmit}
                  disabled={!referralSource || loading}
                  className={authModalPrimaryButtonClassName}
                >
                  {loading ? t('common.loading') : 'Continue'}
                </Button>
              </div>
            ) : (
              /* Form */
              <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-3">
              {/* Toggle between Login/Signup — track uses secondary grey (see Artists list/map tabs); active stays primary blue */}
              <div className="flex gap-1 p-1 bg-secondary rounded-lg">
                <Button
                  type="button"
                  variant={isLogin ? "default" : "ghost"}
                  className={cn(
                    "flex-1 rounded-md",
                    !isLogin && "text-muted-foreground hover:text-foreground hover:bg-transparent"
                  )}
                  onClick={() => setIsLogin(true)}
                >
                  {t('auth.login')}
                </Button>
                <Button
                  type="button"
                  variant={!isLogin ? "default" : "ghost"}
                  className={cn(
                    "flex-1 rounded-md",
                    isLogin && "text-muted-foreground hover:text-foreground hover:bg-transparent"
                  )}
                  onClick={() => setIsLogin(false)}
                >
                  {t('auth.signup')}
                </Button>
              </div>

              {/* Email Field */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-foreground">
                    {t('auth.email')}
                  </label>
                </div>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  className="border-border"
                  required
                />
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-foreground">
                    {t('auth.password')}
                  </label>
                </div>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder={t('auth.passwordPlaceholder')}
                    className="border-border"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Instagram Handle Field - only show for signup */}
              {!isLogin && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-foreground">
                      {t('auth.instagramHandle')}
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowInstagramInfo(true)}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Why?
                    </button>
                  </div>
                  <div>
                    <div className="relative">
                      <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="text"
                        value={formData.instagramHandle}
                        onChange={(e) => handleInputChange('instagramHandle', e.target.value)}
                        placeholder={t('auth.instagramPlaceholder')}
                        className="border-border pl-10"
                        required
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-[var(--card-radius)]">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                className={authModalPrimaryButtonClassName}
                disabled={loading || (isLogin ? !isLoginFormValid() : !isSignupFormValid())}
              >
                {loading 
                  ? t('common.loading')
                  : isLogin 
                    ? t('auth.login')
                    : t('auth.signup')
                }
              </Button>

              {/* Helper Text - Login Mode */}
              {isLogin ? (
                <div className="flex items-center justify-center gap-4 text-xs mt-4">
                  <button
                    type="button"
                    className="text-foreground hover:opacity-80 transition-opacity"
                    onClick={() => setIsLogin(false)}
                  >
                    Don't have an account?
                  </button>
                  <div className="h-4 w-px bg-muted-foreground/30" />
                  <button
                    type="button"
                    className="text-foreground hover:opacity-80 transition-opacity"
                    onClick={() => setShowPasswordReset(true)}
                  >
                    Forgot password?
                  </button>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground text-center">
                  {t('auth.hasAccount')}{' '}
                  <button
                    type="button"
                    className="text-foreground hover:opacity-80 transition-opacity"
                    onClick={() => setIsLogin(true)}
                  >
                    {t('auth.login')}
                  </button>
                </p>
              )}
            </form>
            )}
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>

      {/* Password Reset Dialog - Integrated into AuthModal */}
      <Dialog open={isOpen && showPasswordReset} onOpenChange={dismissible ? handleResetClose : undefined}>
        <DialogPortal>
          <DialogPrimitive.Overlay
            className={cn(
              "fixed inset-0 z-[110] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            )}
          />
          <DialogPrimitive.Content
            className={cn(
              "fixed left-0 right-0 z-[110] border-none bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] rounded-[var(--card-radius)] max-h-[90vh] p-0 overflow-hidden mx-auto",
              "max-w-[calc(var(--max-content-width)+var(--min-side-padding)*2)]"
            )}
            style={{
              top: keyboardHeight > 0 
                ? `calc((100vh - ${keyboardHeight}px) / 2)` 
                : '50%',
              transform: 'translateY(-50%)',
              transition: 'top 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 pb-4">
              <h2 className="text-xl font-semibold text-foreground">
                {resetStep === 'input' ? 'Password Reset 🤔' : 'Password Reset 🫡'}
              </h2>
              {dismissible && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResetClose}
                  className="p-2 -mr-2"
                >
                  <X className="h-5 w-5" />
                  <span className="sr-only">Close</span>
                </Button>
              )}
            </div>

            {/* Content */}
            <div className="px-6 pb-6">
              {resetStep === 'input' ? (
                <form onSubmit={handleResetSubmit} className="space-y-4">
                  {/* Description */}
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Enter your email and we'll send a reset email if an account exists:
                  </p>

                  {/* Email Input */}
                  <div className="space-y-2">
                    <Input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => {
                        setResetEmail(e.target.value)
                        if (error) setError(null)
                      }}
                      placeholder="your@email.com"
                      className="border-border"
                      required
                      disabled={loading}
                    />
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-[var(--card-radius)]">
                      {error}
                    </div>
                  )}

                  {/* Send Button */}
                  <Button
                    type="submit"
                    className={authModalPrimaryButtonClassName}
                    disabled={loading}
                  >
                    {loading ? t('common.loading') : 'Send'}
                  </Button>

                  {/* Back Button */}
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full text-sm"
                    onClick={handleResetBack}
                    disabled={loading}
                  >
                    Back to login
                  </Button>
                </form>
              ) : (
                <div className="space-y-6">
                  {/* Confirmation Message */}
                  <p className="text-base text-foreground leading-relaxed text-center">
                    Sent reset email to {resetEmail} if that account exists
                  </p>

                  {/* Close Button */}
                  <Button
                    type="button"
                    className={authModalPrimaryButtonClassName}
                    onClick={handleResetClose}
                  >
                    Close
                  </Button>
                </div>
              )}
            </div>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>

      {/* Instagram Info Dialog */}
      <SignupFieldInfoDialog
        isOpen={showInstagramInfo}
        onOpenChange={setShowInstagramInfo}
        title="Instagram Handle"
        description="We use your Instagram to contact you if you win a Flash of the Week competition or if there are any issues related to booked appointments!"
      />
    </>
  )
} 
